<script setup lang="ts">
import { computed } from 'vue'
import {
  CONDITION_OPERATORS,
  type ClinicCrfCondition,
  type ClinicCrfConditionLeaf,
  type ClinicCrfField,
} from '@clinic-crf/contract'
import { Braces, RotateCcw } from 'lucide-vue-next'
import { conditionOperandFromText } from '@/lib/editor'

const props = defineProps<{
  modelValue?: ClinicCrfCondition
  fields: ClinicCrfField[]
  title: string
  hint?: string
}>()

const emit = defineEmits<{ 'update:modelValue': [value: ClinicCrfCondition | undefined] }>()

const operatorLabels: Record<string, string> = {
  eq: 'jest równe',
  neq: 'nie jest równe',
  gt: 'jest większe niż',
  gte: 'jest co najmniej',
  lt: 'jest mniejsze niż',
  lte: 'jest najwyżej',
  in: 'należy do listy',
  not_in: 'nie należy do listy',
  present: 'ma wartość',
  missing: 'ma kod braku',
}

function isLeaf(value: ClinicCrfCondition | undefined): value is ClinicCrfConditionLeaf {
  return Boolean(value && !('all' in value) && !('any' in value) && !('not' in value))
}

const complex = computed(() => Boolean(props.modelValue && !isLeaf(props.modelValue)))
const leaf = computed(() => (isLeaf(props.modelValue) ? props.modelValue : undefined))
const hasOperand = computed(() => !['present', 'missing'].includes(leaf.value?.operator ?? ''))
const usesField = computed(() => Boolean(leaf.value?.compare_field))
const operandField = computed(() => props.fields.find((field) => field.path === leaf.value?.field))

function defaultLeaf(): ClinicCrfConditionLeaf {
  return { field: props.fields[0]?.path ?? '', operator: 'eq', value: true }
}

function toggle(enabled: boolean): void {
  emit('update:modelValue', enabled ? defaultLeaf() : undefined)
}

function update(patch: Partial<ClinicCrfConditionLeaf>): void {
  if (!leaf.value) return
  const next = { ...leaf.value, ...patch }
  if (['present', 'missing'].includes(next.operator)) {
    delete next.value
    delete next.compare_field
  } else if (!Object.prototype.hasOwnProperty.call(next, 'value') && !next.compare_field) {
    next.value = ''
  }
  emit('update:modelValue', next)
}

function setOperandMode(mode: 'value' | 'field'): void {
  const current = leaf.value
  if (!current) return
  if (mode === 'field') {
    const next = { ...current, compare_field: props.fields.find((field) => field.path !== current.field)?.path ?? current.field }
    delete next.value
    emit('update:modelValue', next)
  } else {
    const next = { ...current, value: conditionOperandFromText('', current.operator, operandField.value?.type) }
    delete next.compare_field
    emit('update:modelValue', next)
  }
}

function scalarValue(raw: string) {
  return conditionOperandFromText(raw, leaf.value?.operator ?? 'eq', operandField.value?.type)
}

function displayValue(): string {
  const value = leaf.value?.value
  return Array.isArray(value) ? value.join(', ') : String(value ?? '')
}

function summarize(condition: ClinicCrfCondition): string {
  if ('all' in condition) return `Wszystkie: ${condition.all.map(summarize).join(' · ')}`
  if ('any' in condition) return `Dowolny: ${condition.any.map(summarize).join(' · ')}`
  if ('not' in condition) return `Nie (${summarize(condition.not)})`
  const right = condition.compare_field ?? (Array.isArray(condition.value) ? condition.value.join(', ') : String(condition.value ?? ''))
  return `${condition.field} ${operatorLabels[condition.operator]}${right ? ` ${right}` : ''}`
}
</script>

<template>
  <fieldset class="condition-box">
    <legend>{{ title }}</legend>
    <label class="switch-row">
      <input type="checkbox" :checked="Boolean(modelValue)" @change="toggle(($event.target as HTMLInputElement).checked)" />
      <span class="switch" aria-hidden="true"></span>
      <span>Włącz warunek</span>
    </label>
    <p v-if="hint" class="field-hint">{{ hint }}</p>

    <div v-if="modelValue && complex" class="complex-condition">
      <Braces :size="18" aria-hidden="true" />
      <div><strong>Złożony warunek z szablonu</strong><p>{{ summarize(modelValue) }}</p></div>
      <button class="button button-quiet button-small" type="button" @click="emit('update:modelValue', defaultLeaf())">
        <RotateCcw :size="15" /> Zastąp prostym
      </button>
    </div>

    <div v-else-if="leaf" class="condition-grid">
      <label>
        <span>Jeżeli pole</span>
        <select :value="leaf.field" @change="update({ field: ($event.target as HTMLSelectElement).value })">
          <option v-for="field in fields" :key="field.path" :value="field.path">{{ field.label }} · {{ field.path }}</option>
        </select>
      </label>
      <label>
        <span>Operator</span>
        <select :value="leaf.operator" @change="update({ operator: ($event.target as HTMLSelectElement).value as ClinicCrfConditionLeaf['operator'] })">
          <option v-for="operator in CONDITION_OPERATORS" :key="operator" :value="operator">{{ operatorLabels[operator] }}</option>
        </select>
      </label>
      <template v-if="hasOperand">
        <label>
          <span>Porównaj z</span>
          <select :value="usesField ? 'field' : 'value'" @change="setOperandMode(($event.target as HTMLSelectElement).value as 'value' | 'field')">
            <option value="value">wartością</option>
            <option value="field">innym polem</option>
          </select>
        </label>
        <label v-if="usesField">
          <span>Pole porównawcze</span>
          <select :value="leaf.compare_field" @change="update({ compare_field: ($event.target as HTMLSelectElement).value })">
            <option v-for="field in fields" :key="field.path" :value="field.path">{{ field.label }} · {{ field.path }}</option>
          </select>
        </label>
        <label v-else>
          <span>{{ ['in', 'not_in'].includes(leaf.operator) ? 'Wartości po przecinku' : 'Wartość' }}</span>
          <input :value="displayValue()" @input="update({ value: scalarValue(($event.target as HTMLInputElement).value) })" />
        </label>
      </template>
    </div>
  </fieldset>
</template>
